import { useEffect, useRef, useState } from 'react';
import { useOrthodontistSearch } from '../../hooks/useOrthodontistAccounts';
import {
    useAddOrthodontistUser,
    useOrthodontistMembershipsByUser,
    useRemoveOrthodontistUser
} from '../../hooks/useOrthodontistUsers';
import { useToast } from '../../providers/ToastProvider';
import { apiErrorMessage } from '../../utils/apiError';
import { SearchPickerField, SearchPickerList } from './SearchPicker';

interface OrthodontistOrgRow {
    id: string;
    name: string;
}

interface OrthodontistMultiSelectProps {
    userId: string;
    userName: string;
}

// Each pick/remove posts immediately, unlike PracticeMultiSelect's batched save.
export function OrthodontistMultiSelect({ userId, userName }: OrthodontistMultiSelectProps) {
    const { showToast } = useToast();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<OrthodontistOrgRow[]>([]);
    const { memberships, isLoading: isLoadingMemberships } =
        useOrthodontistMembershipsByUser(userId);
    const { results, isLoading } = useOrthodontistSearch(search, open);

    // Seeds `selected` once the memberships fetch resolves; later refetches
    // (triggered by the mutations below) must not clobber local add/remove edits.
    const hasSeeded = useRef(false);
    useEffect(() => {
        if (hasSeeded.current || isLoadingMemberships) return;
        hasSeeded.current = true;
        setSelected(memberships);
    }, [isLoadingMemberships, memberships]);

    const addUser = useAddOrthodontistUser();
    const removeUser = useRemoveOrthodontistUser();
    const isBusy = addUser.isPending || removeUser.isPending;

    const selectedIds = new Set(selected.map((org) => org.id));
    const matches = results.filter((org) => !selectedIds.has(org.id));

    const closeSearch = () => {
        setOpen(false);
        setSearch('');
    };

    const handlePick = async (org: OrthodontistOrgRow) => {
        if (isBusy) return;
        closeSearch();
        try {
            await addUser.mutateAsync({ orthodontistId: org.id, userId });
            setSelected((prev) => [...prev, org]);
            showToast(`${userName} added to ${org.name}`, 'success');
        } catch (error) {
            showToast(apiErrorMessage(error, `Failed to add ${userName} to ${org.name}`), 'error');
        }
    };

    const handleRemove = async (org: OrthodontistOrgRow) => {
        if (isBusy) return;
        try {
            await removeUser.mutateAsync({ orthodontistId: org.id, userId });
            setSelected((prev) => prev.filter((o) => o.id !== org.id));
            showToast(`${userName} removed from ${org.name}`, 'success');
        } catch (error) {
            showToast(
                apiErrorMessage(error, `Failed to remove ${userName} from ${org.name}`),
                'error'
            );
        }
    };

    const status = isLoading
        ? { text: 'Searching…' }
        : matches.length === 0
          ? { text: 'No orthodontist orgs found.' }
          : null;

    return (
        <div className="flex flex-col gap-2">
            <SearchPickerField
                search={search}
                onSearchChange={setSearch}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={closeSearch}
                placeholder="Search an orthodontist org to add"
                status={status}
                options={matches.map((org) => ({
                    key: org.id,
                    title: org.name,
                    subline: `Dr. ${org.drFirstName} ${org.drLastName}`,
                    onPick: () => handlePick({ id: org.id, name: org.name })
                }))}
            />
            <SearchPickerList
                emptyText={isLoadingMemberships ? 'Loading…' : 'No orthodontist orgs linked.'}
                rows={selected.map((org) => ({
                    key: org.id,
                    title: org.name,
                    onRemove: () => handleRemove(org),
                    removeLabel: `Remove ${org.name}`
                }))}
            />
        </div>
    );
}
